/**
 * MapView Component - Enhanced
 *
 * Professional dependency mapping dashboard with:
 * - Multiple visualization tabs (Call Graph, Paragraph Flow, External Calls, Metrics)
 * - D3 force-directed graphs, chord diagrams, and treemaps
 * - Real data integration from code analysis
 * - Interactive filtering and drill-through
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
  MarkerType,
} from '@xyflow/react'
import * as d3 from 'd3'
import {
  ArrowLeft, ArrowRight, Search, Layers, GitFork, Network, BarChart3,
  ChevronRight, ChevronDown, X, RefreshCw, Play, Loader2,
  FileCode, ExternalLink, GitBranch, Activity, Zap, Share2, ArrowRightLeft
} from 'lucide-react'
import { useApplications, useAnalysis } from '../../hooks'
import { applyLayout, LAYOUT_OPTIONS } from '../../utils/graphLayouts'
import {
  getCrossReference,
  getProcedureModel,
  getSummary,
  getJsonArtifact,
  getDependencyMapperResults,
  getDependencyMapperArtifact,
  getJobResults,
} from '../../services/codeAnalysisService'
import '@xyflow/react/dist/style.css'
import './MapView.css'

/**
 * Tab configuration
 */
const TABS = {
  'call-graph': {
    label: 'Program Structure',
    icon: GitBranch,
    description: 'Collapsible call tree — method execution hierarchy',
  },
  'cobol-java': {
    label: 'COBOL → Java',
    icon: ArrowRightLeft,
    description: 'Traceability — which COBOL paragraph became which Java method',
  },
  'coupling': {
    label: 'Coupling',
    icon: Activity,
    description: 'Method coupling — fan-out per method, hotspot detection',
  },
  'external-calls': {
    label: 'External Calls',
    icon: ExternalLink,
    description: 'Inter-program calls and shared data contracts',
  },
  'metrics': {
    label: 'Metrics',
    icon: BarChart3,
    description: 'Transformation metrics — lines, methods, complexity',
  },
}

/**
 * Professional color palette
 */
const COLORS = {
  primary: '#7aa2f7',
  secondary: '#bb9af7',
  success: '#9ece6a',
  warning: '#e0af68',
  danger: '#f7768e',
  info: '#7dcfff',
  depths: ['#7aa2f7', '#9ece6a', '#e0af68', '#f7768e', '#bb9af7'],
  nodes: ['#7aa2f7', '#9ece6a', '#e0af68', '#f7768e', '#bb9af7', '#7dcfff', '#ff9e64', '#73daca'],
}

// Node colors by depth
const DEPTH_COLORS = {
  0: '#7aa2f7', // Entry point
  1: '#9ece6a', // Level 1
  2: '#e0af68', // Level 2
  3: '#bb9af7', // Level 3
  4: '#f7768e', // Level 4+
}

// Custom node for call graph
function CallGraphNode({ data }) {
  const color = DEPTH_COLORS[Math.min(data.depth || 0, 4)]

  return (
    <div className={`call-node ${data.selected ? 'selected' : ''} ${data.isEntry ? 'entry' : ''}`}>
      <div className="call-node-depth" style={{ backgroundColor: color }}>
        {data.depth}
      </div>
      <div className="call-node-content">
        <div className="call-node-name">{data.label}</div>
        <div className="call-node-meta">
          {data.calls} calls • {data.lines || 0} lines
        </div>
      </div>
      {data.hasChildren && (
        <div className="call-node-expand">
          <ChevronRight size={14} />
        </div>
      )}
    </div>
  )
}

const nodeTypes = {
  callgraph: CallGraphNode,
}

/**
 * Paragraph Flow Tab - D3 Force-directed graph
 */
/**
 * COBOL → Java Traceability Tab
 * Maps each COBOL paragraph to its Java method equivalent.
 */
function CobolJavaTab({ procModel, callGraphData, loading }) {
  const rows = useMemo(() => {
    const paragraphs = procModel?.paragraphs || []
    const methods = callGraphData?.classes?.[0]?.methods || []
    const methodMap = new Map(methods.map(m => [m.name, m]))

    // Normalise a COBOL paragraph name to its expected Java camelCase equivalent
    // READ-CUST → readCust,  PROCESS-TRX → processTrx
    function toJava(cobolName) {
      const parts = cobolName.toLowerCase().split('-')
      return parts[0] + parts.slice(1).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('')
    }

    // Matched rows — COBOL paragraph → Java method
    const matched = paragraphs.map(p => {
      const javaName = toJava(p.name)
      const method = methodMap.get(javaName)
      const lines = (p.end_line || 0) - (p.start_line || 0)
      const performs = (p.statements || []).filter(s => s.classification === 'PERFORM' && s.semantic?.target && s.semantic.target !== 'UNTIL').length
      return { cobol: p.name, java: javaName, found: !!method, cobolLines: lines, cobolPerforms: performs, javaCalls: method ? (method.calls || []).length : 0, type: 'mapped' }
    })

    // New Java methods with no COBOL paragraph origin
    const matchedJavaNames = new Set(matched.map(r => r.java))
    const newMethods = methods
      .filter(m => !matchedJavaNames.has(m.name))
      .map(m => ({
        cobol: '—',
        java: m.name,
        found: true,
        cobolLines: 0,
        cobolPerforms: 0,
        javaCalls: (m.calls || []).length,
        type: m.name.startsWith('call') ? 'external-stub' : 'new',
      }))

    return [...matched, ...newMethods]
  }, [procModel, callGraphData])

  if (loading) return <LoadingState />

  const mapped   = rows.filter(r => r.type === 'mapped')
  const newRows  = rows.filter(r => r.type === 'new')
  const stubs    = rows.filter(r => r.type === 'external-stub')

  const empty = !procModel?.paragraphs?.length && !callGraphData?.classes?.length
  if (empty) {
    return (
      <div className="tab-content cobol-java-tab">
        <div className="tab-header"><h3><ArrowRightLeft size={20} /> COBOL → Java</h3></div>
        <div className="empty-state"><p>No traceability data available for this run.</p></div>
      </div>
    )
  }

  const tagStyle = (type) => ({
    display: 'inline-block', padding: '1px 7px', borderRadius: '10px', fontSize: '10px', fontWeight: 600,
    background: type === 'mapped' ? '#9ece6a22' : type === 'external-stub' ? '#f7768e22' : '#e0af6822',
    color:      type === 'mapped' ? '#9ece6a'   : type === 'external-stub' ? '#f7768e'   : '#e0af68',
    border: `1px solid ${type === 'mapped' ? '#9ece6a44' : type === 'external-stub' ? '#f7768e44' : '#e0af6844'}`,
  })

  return (
    <div className="tab-content cobol-java-tab">
      <div className="tab-header">
        <h3><ArrowRightLeft size={20} /> COBOL → Java Traceability</h3>
        <p className="tab-description">
          {mapped.length} COBOL paragraphs → {mapped.length} Java methods &nbsp;·&nbsp;
          {stubs.length} external program stubs &nbsp;·&nbsp;
          {newRows.length} new Java abstractions
        </p>
      </div>

      {/* Summary pills */}
      <div style={{ display: 'flex', gap: '12px', padding: '0 0 16px 0', flexWrap: 'wrap' }}>
        {[
          { label: 'Mapped paragraphs', value: mapped.length, color: '#9ece6a' },
          { label: 'External stubs',    value: stubs.length,  color: '#f7768e' },
          { label: 'New abstractions',  value: newRows.length, color: '#e0af68' },
          { label: 'COBOL lines',       value: mapped.reduce((s, r) => s + r.cobolLines, 0), color: '#7aa2f7' },
        ].map(s => (
          <div key={s.label} style={{ background: '#1e2030', border: '1px solid #2e3347', borderRadius: '8px', padding: '10px 16px', minWidth: '110px' }}>
            <div style={{ fontSize: '20px', fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '11px', color: '#787c99', marginTop: '2px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Traceability table */}
      <div style={{ overflowY: 'auto', maxHeight: '380px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #2e3347' }}>
              {['COBOL Paragraph', 'COBOL Lines', 'PERFORMs', '→', 'Java Method', 'Calls', 'Type'].map(h => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#787c99', fontWeight: 600, fontSize: '11px', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #1e2030', background: i % 2 === 0 ? 'transparent' : '#1a1b2622' }}>
                <td style={{ padding: '7px 10px', color: r.cobol === '—' ? '#3d4456' : '#c0caf5', fontFamily: 'monospace', fontSize: '11px' }}>{r.cobol}</td>
                <td style={{ padding: '7px 10px', color: '#787c99', textAlign: 'right' }}>{r.cobolLines || '—'}</td>
                <td style={{ padding: '7px 10px', color: '#787c99', textAlign: 'right' }}>{r.cobol !== '—' ? r.cobolPerforms : '—'}</td>
                <td style={{ padding: '7px 4px', color: '#3d4456', textAlign: 'center' }}>→</td>
                <td style={{ padding: '7px 10px', color: '#7aa2f7', fontFamily: 'monospace', fontSize: '11px' }}>{r.java}</td>
                <td style={{ padding: '7px 10px', color: '#787c99', textAlign: 'right' }}>{r.javaCalls}</td>
                <td style={{ padding: '7px 10px' }}><span style={tagStyle(r.type)}>{r.type === 'mapped' ? 'mapped' : r.type === 'external-stub' ? 'ext stub' : 'new'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Coupling Tab — method-level fan-out bar chart
 * Shows which methods are the most complex/coupled hotspots.
 */
function CouplingTab({ callGraphData, crossRef, loading }) {
  const svgRef = useRef()
  const containerRef = useRef()

  const methodRows = useMemo(() => {
    const methods = callGraphData?.classes?.[0]?.methods || []
    if (methods.length === 0) return []
    const calledByCount = new Map()
    methods.forEach(m => (m.calls || []).forEach(c => calledByCount.set(c, (calledByCount.get(c) || 0) + 1)))
    return methods.map(m => ({
      name: m.name,
      fanOut: (m.calls || []).length,
      fanIn: calledByCount.get(m.name) || 0,
      isExternalStub: m.name.startsWith('call') && (m.calls || []).length === 0 && m.name.length > 4,
    })).sort((a, b) => b.fanOut - a.fanOut || b.fanIn - a.fanIn)
  }, [callGraphData])

  useEffect(() => {
    if (!methodRows.length || !svgRef.current || !containerRef.current) return

    const container = containerRef.current
    const W = container.clientWidth || 700
    const margin = { top: 10, right: 80, bottom: 30, left: 160 }
    const rowH = 32
    const H = margin.top + margin.bottom + methodRows.length * rowH

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', W).attr('height', H).style('background', 'transparent')

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)
    const innerW = W - margin.left - margin.right
    const maxFanOut = Math.max(...methodRows.map(r => r.fanOut), 1)

    const xScale = d3.scaleLinear().domain([0, maxFanOut]).range([0, innerW * 0.7])
    const yScale = d3.scaleBand().domain(methodRows.map(r => r.name)).range([0, H - margin.top - margin.bottom]).padding(0.25)

    const getColor = r => r.isExternalStub ? '#f7768e' : r.fanOut >= 5 ? '#e0af68' : r.fanOut >= 2 ? '#7aa2f7' : '#3d4456'

    // Bars
    g.selectAll('.coup-bar')
      .data(methodRows).enter()
      .append('rect').attr('class', 'coup-bar')
      .attr('x', 0).attr('y', d => yScale(d.name))
      .attr('width', d => Math.max(xScale(d.fanOut), d.fanOut > 0 ? 4 : 0))
      .attr('height', yScale.bandwidth())
      .attr('rx', 3).attr('fill', d => getColor(d))
      .attr('opacity', 0.85)

    // Fan-out value label
    g.selectAll('.coup-val')
      .data(methodRows).enter()
      .append('text').attr('class', 'coup-val')
      .attr('x', d => xScale(d.fanOut) + 6)
      .attr('y', d => yScale(d.name) + yScale.bandwidth() / 2)
      .attr('dominant-baseline', 'central')
      .attr('font-size', 11).attr('fill', '#787c99')
      .text(d => d.fanOut > 0 ? `${d.fanOut} call${d.fanOut !== 1 ? 's' : ''}` : 'leaf')

    // Y axis labels (method names)
    g.selectAll('.coup-label')
      .data(methodRows).enter()
      .append('text').attr('class', 'coup-label')
      .attr('x', -8).attr('y', d => yScale(d.name) + yScale.bandwidth() / 2)
      .attr('text-anchor', 'end').attr('dominant-baseline', 'central')
      .attr('font-size', 11).attr('font-family', 'monospace')
      .attr('fill', d => getColor(d))
      .text(d => d.name)

    // fan-in indicators (small right-side badges)
    g.selectAll('.fanin-badge')
      .data(methodRows.filter(r => r.fanIn > 0)).enter()
      .append('text').attr('class', 'fanin-badge')
      .attr('x', innerW * 0.75)
      .attr('y', d => yScale(d.name) + yScale.bandwidth() / 2)
      .attr('dominant-baseline', 'central')
      .attr('font-size', 10).attr('fill', '#565f89')
      .text(d => `← ${d.fanIn}`)

  }, [methodRows])

  if (loading) return <LoadingState />

  if (!methodRows.length) {
    return (
      <div className="tab-content coupling-tab">
        <div className="tab-header"><h3><Activity size={20} /> Coupling</h3></div>
        <div className="empty-state"><p>No coupling data available for this run.</p></div>
      </div>
    )
  }

  const hotspot = methodRows[0]
  const externalStubs = methodRows.filter(r => r.isExternalStub)
  const leafMethods = methodRows.filter(r => r.fanOut === 0 && !r.isExternalStub)

  return (
    <div className="tab-content coupling-tab">
      <div className="tab-header">
        <h3><Activity size={20} /> Method Coupling</h3>
        <p className="tab-description">
          Fan-out per method (outgoing calls). Bar length = coupling strength. ← = how many methods call this one.
        </p>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: '12px', padding: '0 0 16px 0', flexWrap: 'wrap' }}>
        {[
          { label: 'Hotspot',          value: hotspot.name,         color: '#e0af68' },
          { label: 'Max fan-out',      value: hotspot.fanOut,       color: '#e0af68' },
          { label: 'External stubs',   value: externalStubs.length, color: '#f7768e' },
          { label: 'Leaf methods',     value: leafMethods.length,   color: '#565f89' },
        ].map(s => (
          <div key={s.label} style={{ background: '#1e2030', border: '1px solid #2e3347', borderRadius: '8px', padding: '10px 16px', minWidth: '110px' }}>
            <div style={{ fontSize: s.label === 'Hotspot' ? '14px' : '20px', fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '11px', color: '#787c99', marginTop: '2px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div ref={containerRef} style={{ overflowY: 'auto', maxHeight: '420px' }}>
        <svg ref={svgRef} style={{ display: 'block' }} />
      </div>

      <div style={{ fontSize: '11px', color: '#565f89', marginTop: '8px' }}>
        <span style={{ color: '#e0af68' }}>■</span> High coupling (5+) &nbsp;
        <span style={{ color: '#7aa2f7' }}>■</span> Normal (2–4) &nbsp;
        <span style={{ color: '#f7768e' }}>■</span> External stub &nbsp;
        <span style={{ color: '#3d4456' }}>■</span> Leaf method
      </div>
    </div>
  )
}

/**
 * External Calls Tab - Chord diagram
 */
function ExternalCallsTab({ dependencyGraph, crossRef, mainProgram, loading, error }) {
  const svgRef = useRef()
  const containerRef = useRef()

  useEffect(() => {
    // Need either dependencyGraph or crossRef
    if ((!dependencyGraph && !crossRef) || !svgRef.current) return

    const container = containerRef.current
    const width = container.clientWidth || 700
    const height = container.clientHeight || 600
    const radius = Math.min(width, height) / 2 - 100

    let nodes = []
    let links = []

    // Use dependency graph data if available
    if (dependencyGraph?.nodes?.length > 0) {
      const graphNodes = dependencyGraph.nodes
      const graphEdges = dependencyGraph.edges || []

      // Find main program (highest fan_out)
      const mainNode = graphNodes.reduce((max, n) =>
        (n.fan_out || 0) > (max?.fan_out || 0) ? n : max, graphNodes[0])

      // Get copybooks and external calls from edges
      const copyEdges = graphEdges.filter(e => e.type === 'COPY' && e.from === mainNode.id)
      const callEdges = graphEdges.filter(e => e.type === 'CALL' && e.from === mainNode.id)
      const copyTargets = [...new Set(copyEdges.map(e => e.to))]
      const callTargets = [...new Set(callEdges.map(e => e.to))]

      // Build chord diagram nodes
      nodes = [
        { id: mainNode.id, type: 'main', group: 0 },
        ...copyTargets.slice(0, 15).map(c => ({ id: c, type: 'copybook', group: 1 })),
        ...callTargets.slice(0, 15).map(c => ({ id: c, type: 'external', group: 2 })),
      ]

      links = [
        ...copyTargets.slice(0, 15).map(c => ({ source: 0, target: nodes.findIndex(n => n.id === c), value: 1 })),
        ...callTargets.slice(0, 15).map(c => ({ source: 0, target: nodes.findIndex(n => n.id === c), value: 2 })),
      ].filter(l => l.target > 0)
    } else {
      // Fallback to crossRef — normalize key lookup so "CUSTUPD.cbl" style keys are found
      const callGraph = crossRef?.call_graph || {}
      const copybooks = Object.keys(crossRef?.copybook_usage || {})
      const resolvedKey = Object.keys(callGraph).find(k =>
        k === mainProgram ||
        k === `${mainProgram}.CBL` ||
        k === `${mainProgram}.cbl` ||
        k.toLowerCase().replace(/\.(cbl|cobol)$/, '') ===
          (mainProgram || '').toLowerCase().replace(/\.(cbl|cobol)$/, '')
      ) || Object.keys(callGraph).find(k => (callGraph[k] || []).length > 0) || Object.keys(callGraph)[0]
      const callList = resolvedKey ? (callGraph[resolvedKey] || []) : []
      const externalCalls = Array.isArray(callList) ? [...new Set(callList)] : []
      const displayMain = resolvedKey?.replace(/\.(cbl|cobol)$/i, '').toUpperCase() || mainProgram || 'MAIN'

      nodes = [
        { id: displayMain, type: 'main', group: 0 },
        ...copybooks.slice(0, 15).map(c => ({ id: c, type: 'copybook', group: 1 })),
        ...externalCalls.slice(0, 15).map(c => ({ id: c, type: 'external', group: 2 })),
      ]

      links = [
        ...copybooks.slice(0, 15).map(c => ({ source: 0, target: nodes.findIndex(n => n.id === c), value: 1 })),
        ...externalCalls.slice(0, 15).map(c => ({ source: 0, target: nodes.findIndex(n => n.id === c), value: 2 })),
      ].filter(l => l.target > 0)
    }

    if (nodes.length <= 1) return // Nothing to visualize

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', width).attr('height', height)

    const g = svg.append('g').attr('transform', `translate(${width/2},${height/2})`)

    // Create matrix for chord diagram
    const matrix = Array(nodes.length).fill(null).map(() => Array(nodes.length).fill(0))
    links.forEach(l => {
      matrix[l.source][l.target] = l.value
      matrix[l.target][l.source] = l.value
    })

    const chord = d3.chord().padAngle(0.05).sortSubgroups(d3.descending)(matrix)

    const arc = d3.arc().innerRadius(radius - 20).outerRadius(radius)
    const ribbon = d3.ribbon().radius(radius - 25)

    const color = d3.scaleOrdinal()
      .domain([0, 1, 2])
      .range([COLORS.primary, COLORS.success, COLORS.warning])

    // Draw arcs
    const group = g.append('g')
      .selectAll('g')
      .data(chord.groups)
      .enter()
      .append('g')

    group.append('path')
      .attr('fill', d => color(nodes[d.index].group))
      .attr('stroke', '#1a1b26')
      .attr('d', arc)

    group.append('text')
      .each(d => { d.angle = (d.startAngle + d.endAngle) / 2 })
      .attr('dy', '.35em')
      .attr('transform', d => `
        rotate(${(d.angle * 180 / Math.PI - 90)})
        translate(${radius + 10})
        ${d.angle > Math.PI ? 'rotate(180)' : ''}
      `)
      .attr('text-anchor', d => d.angle > Math.PI ? 'end' : null)
      .attr('font-size', '10px')
      .attr('fill', '#a9b1d6')
      .text(d => nodes[d.index].id.length > 12 ? nodes[d.index].id.substring(0, 10) + '..' : nodes[d.index].id)

    // Draw ribbons
    g.append('g')
      .attr('fill-opacity', 0.6)
      .selectAll('path')
      .data(chord)
      .enter()
      .append('path')
      .attr('d', ribbon)
      .attr('fill', d => color(nodes[d.source.index].group))
      .attr('stroke', '#1a1b26')

    // Legend
    const legend = svg.append('g').attr('transform', `translate(20, 20)`)
    const legendItems = [
      { label: 'Main Program', color: COLORS.primary },
      { label: 'Copybooks (COPY)', color: COLORS.success },
      { label: 'External Calls (CALL)', color: COLORS.warning },
    ]
    legendItems.forEach((item, i) => {
      const lg = legend.append('g').attr('transform', `translate(0, ${i * 22})`)
      lg.append('rect').attr('width', 14).attr('height', 14).attr('rx', 3).attr('fill', item.color)
      lg.append('text').attr('x', 20).attr('y', 11).attr('fill', '#a9b1d6').attr('font-size', '11px').text(item.label)
    })

  }, [dependencyGraph, crossRef, mainProgram])

  if (loading) return <LoadingState />

  // Check if we have any data to display
  const hasData = dependencyGraph?.nodes?.length > 0 || crossRef?.call_graph || crossRef?.copybook_usage

  if (!hasData) {
    return (
      <div className="tab-content external-calls-tab">
        <div className="tab-header">
          <h3><ExternalLink size={20} /> External Dependencies</h3>
        </div>
        <div className="empty-state">
          <p>No external dependency data available for this run.</p>
        </div>
      </div>
    )
  }

  // Calculate counts for description
  let copybookCount = 0
  let callCount = 0

  if (dependencyGraph?.edges) {
    const mainNode = dependencyGraph.nodes?.reduce((max, n) =>
      (n.fan_out || 0) > (max?.fan_out || 0) ? n : max, dependencyGraph.nodes[0])
    const mainId = mainNode?.id
    copybookCount = new Set(dependencyGraph.edges.filter(e => e.type === 'COPY' && e.from === mainId).map(e => e.to)).size
    callCount = new Set(dependencyGraph.edges.filter(e => e.type === 'CALL' && e.from === mainId).map(e => e.to)).size
  } else if (crossRef) {
    const callGraph = crossRef.call_graph || {}
    const rawKey = Object.keys(callGraph).find(k =>
      k === mainProgram ||
      k === `${mainProgram}.CBL` ||
      k === `${mainProgram}.cbl` ||
      k.toLowerCase().replace(/\.(cbl|cobol)$/, '') ===
        (mainProgram || '').toLowerCase().replace(/\.(cbl|cobol)$/, '')
    ) || Object.keys(callGraph).find(k => (callGraph[k] || []).length > 0) || Object.keys(callGraph)[0]
    const callList = rawKey ? (callGraph[rawKey] || []) : []
    callCount = Array.isArray(callList) ? [...new Set(callList)].length : 0
    copybookCount = Object.keys(crossRef.copybook_usage || {}).length
  }

  return (
    <div className="tab-content external-calls-tab">
      <div className="tab-header">
        <h3><ExternalLink size={20} /> External Dependencies</h3>
        <p className="tab-description">
          Chord diagram showing relationships between main program, {copybookCount} copybooks, and {callCount} external program calls.
        </p>
      </div>
      <div className="chart-wrapper" ref={containerRef}>
        <svg ref={svgRef}></svg>
      </div>
    </div>
  )
}

/**
 * Metrics Tab - Transformation metrics using real call graph + line inventory data
 */
function MetricsTab({ dependencyGraph, procModel, callGraphData, summary, lineInventory, loading, error }) {
  const svgRef = useRef()
  const containerRef = useRef()

  // --- Derive real numbers from authoritative sources ---
  const methods = callGraphData?.classes?.[0]?.methods || []
  const paragraphs = procModel?.paragraphs || []

  // lineInventory = custupd_line_inventory.json — field is source_line_count
  const cobolLines = lineInventory?.source_line_count
    || summary?.source_line_count
    || 0

  // Java line count: not pre-computed in any currently-fetched artifact
  // Show if explicitly provided, otherwise 0 → displays as "—"
  const javaLines = lineInventory?.java_lines || summary?.java_line_count || 0
  const cobolParagraphs = paragraphs.length || summary?.paragraph_count || 0
  const javaMethods = methods.length || summary?.method_count || 0

  // For treemap: group methods by prefix (e.g. "process", "read", "validate")
  // Size = estimated java lines or call_count+1 as proxy
  const methodGroups = {}
  methods.forEach(m => {
    const raw = m.name || ''
    // camelCase split: "processTrx" → prefix "process", "readCust" → "read"
    const match = raw.match(/^([a-z]+)/)
    const prefix = match ? match[1] : 'other'
    const group = prefix.charAt(0).toUpperCase() + prefix.slice(1)
    if (!methodGroups[group]) methodGroups[group] = { name: group, children: [] }
    const callCount = m.call_count || m.calls?.length || 0
    methodGroups[group].children.push({
      name: raw,
      value: Math.max(callCount * 4 + 8, 8),  // scale for treemap visibility
      calls: callCount,
      isExternal: raw.startsWith('call'),
    })
  })

  // If no methods, fall back to paragraphs for treemap
  let hierarchyData
  if (Object.keys(methodGroups).length > 0) {
    hierarchyData = { name: 'Java Methods', children: Object.values(methodGroups) }
  } else if (paragraphs.length > 0) {
    const prefixGroups = {}
    paragraphs.forEach(p => {
      const prefix = p.name.split('-')[0] || 'OTHER'
      if (!prefixGroups[prefix]) prefixGroups[prefix] = { name: prefix, children: [] }
      const lineCount = Math.max((p.end_line || 0) - (p.start_line || 0), 5)
      prefixGroups[prefix].children.push({ name: p.name, value: lineCount, calls: 0, isExternal: false })
    })
    hierarchyData = { name: 'COBOL Paragraphs', children: Object.values(prefixGroups) }
  } else {
    hierarchyData = null
  }

  useEffect(() => {
    if (!hierarchyData || !svgRef.current || !containerRef.current) return

    const container = containerRef.current
    const width = container.clientWidth || 800
    const height = 380

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', width).attr('height', height)

    const color = d3.scaleOrdinal().range(COLORS.nodes)

    const root = d3.hierarchy(hierarchyData)
      .sum(d => d.value)
      .sort((a, b) => b.value - a.value)

    d3.treemap()
      .size([width, height])
      .paddingOuter(4)
      .paddingInner(2)
      .paddingTop(18)
      (root)

    // Group labels
    svg.selectAll('.group-label')
      .data(root.children || [])
      .enter()
      .append('text')
      .attr('class', 'group-label')
      .attr('x', d => d.x0 + 4)
      .attr('y', d => d.y0 + 12)
      .attr('font-size', '10px')
      .attr('font-weight', '700')
      .attr('fill', '#e2e8f0')
      .attr('text-transform', 'uppercase')
      .text(d => d.data.name)

    const cell = svg.selectAll('.leaf')
      .data(root.leaves())
      .enter()
      .append('g')
      .attr('class', 'leaf')
      .attr('transform', d => `translate(${d.x0},${d.y0})`)

    cell.append('rect')
      .attr('width', d => Math.max(d.x1 - d.x0, 0))
      .attr('height', d => Math.max(d.y1 - d.y0, 0))
      .attr('fill', d => d.data.isExternal ? '#e53e3e' : color(d.parent.data.name))
      .attr('stroke', '#1a1b26')
      .attr('rx', 3)
      .style('opacity', 0.82)

    cell.append('title').text(d =>
      `${d.data.name}\nCalls: ${d.data.calls}\n${d.data.isExternal ? '🔴 External stub' : ''}`.trim()
    )

    cell.filter(d => (d.x1 - d.x0) > 55 && (d.y1 - d.y0) > 22)
      .append('text')
      .attr('x', 4)
      .attr('y', 14)
      .attr('font-size', '10px')
      .attr('fill', '#1a1b26')
      .attr('font-weight', '600')
      .text(d => d.data.name.length > 13 ? d.data.name.substring(0, 11) + '..' : d.data.name)

    cell.filter(d => (d.x1 - d.x0) > 55 && (d.y1 - d.y0) > 36)
      .append('text')
      .attr('x', 4)
      .attr('y', 26)
      .attr('font-size', '9px')
      .attr('fill', '#1a1b26')
      .style('opacity', 0.7)
      .text(d => d.data.calls > 0 ? `${d.data.calls} calls` : '')

  }, [callGraphData, procModel])

  if (loading) return <LoadingState />

  const hasData = methods.length > 0 || paragraphs.length > 0

  if (!hasData && !cobolLines) {
    return (
      <div className="tab-content metrics-tab">
        <div className="tab-header">
          <h3><BarChart3 size={20} /> Transformation Metrics</h3>
        </div>
        <div className="empty-state">
          <p>No metrics data available for this run.</p>
        </div>
      </div>
    )
  }

  const avgLinesPerPara = cobolParagraphs > 0 ? Math.round(cobolLines / cobolParagraphs) : 0
  const transformRatio = cobolLines > 0 && javaLines > 0
    ? (javaLines / cobolLines).toFixed(2)
    : null
  const externalStubs = methods.filter(m => m.name?.startsWith('call')).length
  const performCount = paragraphs.reduce((sum, p) =>
    sum + (p.statements?.filter(s => s.classification === 'PERFORM').length || 0), 0)

  return (
    <div className="tab-content metrics-tab">
      <div className="tab-header">
        <h3><BarChart3 size={20} /> Transformation Metrics</h3>
        <p className="tab-description">
          Real transformation statistics — COBOL source vs generated Java output. Treemap shows Java method distribution by call activity.
        </p>
      </div>

      {/* Stat cards row 1: source vs output */}
      <div className="metrics-grid">
        <div className="metric-card highlight">
          <FileCode size={24} />
          <span className="metric-value">{cobolLines > 0 ? cobolLines.toLocaleString() : '—'}</span>
          <span className="metric-label">COBOL Lines</span>
        </div>
        <div className="metric-card highlight" style={{ borderColor: '#48bb78' }}>
          <FileCode size={24} style={{ color: '#48bb78' }} />
          <span className="metric-value" style={{ color: '#48bb78' }}>{javaLines > 0 ? javaLines.toLocaleString() : '—'}</span>
          <span className="metric-label">Java Lines</span>
        </div>
        <div className="metric-card">
          <GitFork size={24} />
          <span className="metric-value">{cobolParagraphs || '—'}</span>
          <span className="metric-label">COBOL Paragraphs</span>
        </div>
        <div className="metric-card">
          <Activity size={24} />
          <span className="metric-value">{javaMethods || '—'}</span>
          <span className="metric-label">Java Methods</span>
        </div>
      </div>

      {/* Stat cards row 2: derived metrics */}
      <div className="metrics-grid" style={{ marginTop: '0.75rem' }}>
        <div className="metric-card">
          <Zap size={24} />
          <span className="metric-value">{avgLinesPerPara || '—'}</span>
          <span className="metric-label">Avg Lines / Para</span>
        </div>
        <div className="metric-card">
          <ArrowRight size={24} />
          <span className="metric-value">{transformRatio ? `${transformRatio}×` : '—'}</span>
          <span className="metric-label">Transform Ratio</span>
        </div>
        <div className="metric-card">
          <Share2 size={24} />
          <span className="metric-value">{performCount || (cobolParagraphs > 0 ? '—' : '—')}</span>
          <span className="metric-label">PERFORM Calls</span>
        </div>
        <div className="metric-card" style={{ borderColor: externalStubs > 0 ? '#e53e3e' : undefined }}>
          <ExternalLink size={24} style={{ color: externalStubs > 0 ? '#e53e3e' : undefined }} />
          <span className="metric-value" style={{ color: externalStubs > 0 ? '#e53e3e' : undefined }}>{externalStubs}</span>
          <span className="metric-label">External Stubs</span>
        </div>
      </div>

      {/* Transformation ratio bar */}
      {cobolLines > 0 && javaLines > 0 && (
        <div className="treemap-container" style={{ marginTop: '1rem' }}>
          <h4 style={{ marginBottom: '0.5rem', fontSize: '0.85rem', color: '#a0aec0' }}>
            Lines of Code: COBOL → Java
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: 80, fontSize: '0.75rem', color: '#a0aec0', textAlign: 'right' }}>COBOL</span>
              <div style={{ flex: 1, background: '#2d3748', borderRadius: 4, height: 20, position: 'relative' }}>
                <div style={{
                  width: '100%', height: '100%', background: '#4a5568',
                  borderRadius: 4, display: 'flex', alignItems: 'center', paddingLeft: 8
                }}>
                  <span style={{ fontSize: '0.72rem', color: '#e2e8f0', fontWeight: 600 }}>{cobolLines} lines</span>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: 80, fontSize: '0.75rem', color: '#a0aec0', textAlign: 'right' }}>Java</span>
              <div style={{ flex: 1, background: '#2d3748', borderRadius: 4, height: 20, position: 'relative' }}>
                <div style={{
                  width: `${Math.min((javaLines / Math.max(cobolLines, javaLines)) * 100, 100)}%`,
                  height: '100%', background: '#276749',
                  borderRadius: 4, minWidth: 80, display: 'flex', alignItems: 'center', paddingLeft: 8
                }}>
                  <span style={{ fontSize: '0.72rem', color: '#c6f6d5', fontWeight: 600 }}>{javaLines} lines</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Treemap */}
      {hasData && (
        <div className="treemap-container" style={{ marginTop: '1rem' }}>
          <h4 style={{ marginBottom: '0.5rem', fontSize: '0.85rem', color: '#a0aec0' }}>
            {methods.length > 0 ? 'Java Method Distribution (sized by call activity)' : 'COBOL Paragraph Distribution'}
          </h4>
          <div className="chart-wrapper" ref={containerRef}>
            <svg ref={svgRef}></svg>
          </div>
          {methods.length > 0 && (
            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.72rem', color: '#a0aec0' }}>
                <span style={{ display: 'inline-block', width: 10, height: 10, background: '#e53e3e', borderRadius: 2, marginRight: 4 }}></span>
                External program stubs
              </span>
              <span style={{ fontSize: '0.72rem', color: '#a0aec0' }}>
                Larger blocks = more internal call activity
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Convert call_graph.json methods into a d3-hierarchy-compatible tree.
 * The method graph is a DAG (some methods called by multiple parents) —
 * we convert to a tree by tracking visited nodes per-path to avoid cycles,
 * and marking re-visited nodes as truncated stubs.
 */
function buildProgramTree(callGraphData, crossRef, mainProgram) {
  if (callGraphData?.classes?.[0]?.methods?.length > 0) {
    const methods = callGraphData.classes[0].methods
    const methodMap = new Map(methods.map(m => [m.name, m]))
    const calledByAny = new Set(methods.flatMap(m => m.calls || []))
    const root = methods.find(m => !calledByAny.has(m.name)) || methods[0]

    function buildNode(name, visited, depth) {
      const isCycle = visited.has(name)
      if (depth > 8 || isCycle) {
        return { name, callCount: 0, isLeaf: true, isCycle }
      }
      const m = methodMap.get(name)
      if (!m) return { name, callCount: 0, isLeaf: true }
      const next = new Set(visited).add(name)
      const children = (m.calls || []).map(c => buildNode(c, next, depth + 1))
      // Identify external program stubs — methods named call* with no body calls
      const isExternalStub = name.startsWith('call') && name.length > 4 && (m.calls || []).length === 0
      return {
        name,
        callCount: (m.calls || []).length,
        isExternalStub,
        children: children.length > 0 ? children : undefined,
      }
    }
    return buildNode(root.name, new Set(), 0)
  }

  // Fallback: inter-program CALL statements from cross_reference
  if (crossRef?.call_graph) {
    const cg = crossRef.call_graph
    const key = Object.keys(cg).find(k => (cg[k] || []).length > 0) || Object.keys(cg)[0]
    const label = key?.replace(/\.(cbl|cobol)$/i, '').toUpperCase() || mainProgram || 'PROGRAM'
    const calls = key ? (cg[key] || []) : []
    return {
      name: label,
      callCount: calls.length,
      children: calls.map(c => ({ name: c, callCount: 0, isExternalStub: true }))
    }
  }
  return null
}

/**
 * Program Structure Tab — D3 collapsible horizontal tree
 */
function ProgramStructureTab({ callGraphData, crossRef, mainProgram, loading }) {
  const svgRef = useRef()
  const containerRef = useRef()

  const treeData = useMemo(
    () => buildProgramTree(callGraphData, crossRef, mainProgram),
    [callGraphData, crossRef, mainProgram]
  )

  useEffect(() => {
    if (!treeData || !svgRef.current || !containerRef.current) return

    const container = containerRef.current
    const W = container.clientWidth  || 900
    const H = container.clientHeight || 520
    const margin = { top: 20, right: 170, bottom: 20, left: 100 }

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', W).attr('height', H).style('background', 'transparent')

    // Pan + zoom
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)
    svg.call(
      d3.zoom().scaleExtent([0.25, 3]).on('zoom', e =>
        g.attr('transform', `translate(${margin.left + e.transform.x},${margin.top + e.transform.y}) scale(${e.transform.k})`)
      )
    )

    const depthColors = ['#7aa2f7', '#9ece6a', '#e0af68', '#bb9af7', '#7dcfff', '#ff9e64', '#73daca']
    const getColor = d => d.data.isExternalStub ? '#f7768e' : depthColors[Math.min(d.depth, depthColors.length - 1)]

    const treeLayout = d3.tree().nodeSize([52, 230]).separation((a, b) => a.parent === b.parent ? 1 : 1.3)
    const root = d3.hierarchy(treeData)

    // Collapse state — initially all expanded
    function collapse(d) { if (d.children) { d._children = d.children; d.children = null } }
    function expand(d)   { if (d._children) { d.children = d._children; d._children = null } }

    function update() {
      treeLayout(root)

      // Center vertically
      let minX = Infinity, maxX = -Infinity
      root.each(d => { if (d.x < minX) minX = d.x; if (d.x > maxX) maxX = d.x })
      const cy = (H - margin.top - margin.bottom) / 2 - (minX + maxX) / 2

      // ── Links ──────────────────────────────────────────────────────────────
      const links = g.selectAll('.ps-link').data(root.links(), d => d.target.data.name + d.source.data.name)

      links.enter().append('path')
        .attr('class', 'ps-link')
        .attr('fill', 'none')
        .attr('stroke', '#2e3347')
        .attr('stroke-width', 1.5)
        .merge(links)
        .attr('d', d3.linkHorizontal().x(d => d.y).y(d => d.x + cy))

      links.exit().remove()

      // ── Nodes ──────────────────────────────────────────────────────────────
      const nodes = g.selectAll('.ps-node').data(root.descendants(), d => d.data.name)

      const enter = nodes.enter().append('g')
        .attr('class', 'ps-node')
        .attr('transform', d => `translate(${d.y},${d.x + cy})`)
        .style('cursor', d => (d.children || d._children) ? 'pointer' : 'default')
        .on('click', (_, d) => {
          if (d.children) collapse(d); else expand(d)
          update()
        })

      // Pill background
      enter.append('rect')
        .attr('x', -72).attr('y', -14).attr('width', 144).attr('height', 28).attr('rx', 14)
        .attr('fill', d => `${getColor(d)}15`)
        .attr('stroke', d => getColor(d))
        .attr('stroke-width', d => d.depth === 0 ? 2 : 1)
        .attr('stroke-dasharray', d => d.data.isCycle ? '4,3' : 'none')

      // Label
      enter.append('text')
        .attr('text-anchor', 'middle').attr('dominant-baseline', 'central')
        .attr('font-size', d => d.depth === 0 ? 13 : 11)
        .attr('font-weight', d => d.depth === 0 ? '700' : '400')
        .attr('fill', d => getColor(d))
        .attr('pointer-events', 'none')
        .text(d => d.data.name.length > 17 ? d.data.name.slice(0, 15) + '…' : d.data.name)

      // Collapse indicator dot (right edge, only on nodes with children)
      enter.filter(d => d.children || d._children)
        .append('circle')
        .attr('cx', 72).attr('cy', 0).attr('r', 5)
        .attr('fill', d => getColor(d))
        .attr('opacity', 0.7)

      // Call-count badge (top-right, only if > 0)
      enter.filter(d => (d.data.callCount || 0) > 0)
        .append('g').attr('class', 'badge')
        .call(bg => {
          bg.append('circle').attr('cx', 78).attr('cy', -16).attr('r', 10).attr('fill', d => getColor(d))
          bg.append('text')
            .attr('x', 78).attr('y', -16)
            .attr('text-anchor', 'middle').attr('dominant-baseline', 'central')
            .attr('font-size', 9).attr('font-weight', '700').attr('fill', '#1a1b26')
            .attr('pointer-events', 'none')
            .text(d => d.data.callCount)
        })

      // Merge + reposition existing nodes
      nodes.merge(enter)
        .attr('transform', d => `translate(${d.y},${d.x + cy})`)

      nodes.exit().remove()

      // Tooltip
      const tip = d3.select(containerRef.current).selectAll('.ps-tip')
        .data([null]).join('div').attr('class', 'ps-tip')
        .style('position', 'absolute').style('background', '#1e2030')
        .style('border', '1px solid #3d4456').style('border-radius', '6px')
        .style('padding', '7px 11px').style('font-size', '12px').style('color', '#c0caf5')
        .style('pointer-events', 'none').style('opacity', '0').style('z-index', '100')
        .style('max-width', '200px')

      g.selectAll('.ps-node')
        .on('mouseover', (event, d) => {
          const kind = d.data.isExternalStub ? '🔴 External program call'
            : d.depth === 0 ? '⚡ Entry point'
            : d.data.isCycle ? '🔁 Cycle (truncated)'
            : '⚙️ Method'
          tip.html(`<strong>${d.data.name}</strong><br/>${kind}${d.data.callCount > 0 ? `<br/>${d.data.callCount} outgoing calls` : ''}`)
            .style('opacity', '1')
            .style('left', `${event.offsetX + 14}px`)
            .style('top', `${event.offsetY - 10}px`)
        })
        .on('mousemove', event => {
          tip.style('left', `${event.offsetX + 14}px`).style('top', `${event.offsetY - 10}px`)
        })
        .on('mouseout', () => tip.style('opacity', '0'))
    }

    update()
  }, [treeData])

  if (loading) return <LoadingState />

  if (!treeData) {
    return (
      <div className="tab-content program-structure-tab">
        <div className="tab-header"><h3><GitBranch size={20} /> Program Structure</h3></div>
        <div className="empty-state"><p>No program structure data available for this run.</p></div>
      </div>
    )
  }

  return (
    <div className="tab-content program-structure-tab">
      <div className="tab-header">
        <h3><GitBranch size={20} /> Program Structure</h3>
        <p className="tab-description">
          Left-to-right call tree. Click any node to collapse/expand. Badge = outgoing calls. <span style={{color:'#f7768e'}}>Red</span> = external program stub.
        </p>
      </div>
      <div
        ref={containerRef}
        style={{ position: 'relative', height: '520px', overflow: 'hidden', borderRadius: '8px' }}
      >
        <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />
      </div>
    </div>
  )
}

/**
 * Summary Panel
 */
function SummaryPanel({ stats, loading, error }) {
  if (loading) {
    return (
      <div className="summary-panel">
        <div className="panel-section">
          <h4>Summary</h4>
          <div className="loading-placeholder">Loading...</div>
        </div>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="summary-panel">
        <div className="panel-section">
          <h4>Summary</h4>
          <div className="loading-placeholder">
            {error || 'No data available. Run Code Analysis first.'}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="summary-panel">
      <div className="panel-section">
        <h4><Activity size={16} /> Quick Stats</h4>
        <div className="stats-list">
          <div className="stat-row">
            <span className="stat-label">Total Lines</span>
            <span className="stat-value">{stats.totalLines?.toLocaleString()}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Paragraphs</span>
            <span className="stat-value">{stats.paragraphs}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">External Calls</span>
            <span className="stat-value">{stats.externalCalls}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Copybooks</span>
            <span className="stat-value">{stats.copybooks}</span>
          </div>
        </div>
      </div>

      {stats.topParagraphs?.length > 0 && (
        <div className="panel-section">
          <h4><GitFork size={16} /> Top Paragraphs</h4>
          <div className="top-items">
            {stats.topParagraphs.map((p, i) => (
              <div key={i} className="top-item">
                <span className="item-name">{p.name}</span>
                <span className="item-value">{p.lines} lines</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Loading state
 */
function LoadingState() {
  return (
    <div className="loading-state">
      <Loader2 size={32} className="spin" />
      <span>Loading analysis data...</span>
    </div>
  )
}

/**
 * Main MapView component
 */
function MapViewInner({ onBack, analysisContext = null }) {
  const [activeTab, setActiveTab] = useState('call-graph')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Data state - both dependency mapper and code analysis
  const [dependencyGraph, setDependencyGraph] = useState(null)
  const [callGraphData, setCallGraphData] = useState(null)   // call_graph.json — method-level hierarchy
  const [crossReference, setCrossReference] = useState(null)
  const [procedureModel, setProcedureModel] = useState(null)
  const [summary, setSummary] = useState(null)
  const [lineInventory, setLineInventory] = useState(null)

  const jobContext = analysisContext
  const { currentApplication } = useApplications()
  const { analyzing, progress, runDependencyMapping } = useAnalysis(currentApplication?.id)

  // Fetch all data - dependency mapper first, then code analysis
  const fetchData = useCallback(async () => {
    if (!jobContext?.jobId) return

    setLoading(true)
    setError(null)

    try {
      // Step 1: Resolve authoritative baseName from job record.
      // jobContext.baseName comes from selectedAppName.toLowerCase() which may not match
      // the actual COBOL program name used as artifact filename prefix.
      let baseName = jobContext.baseName
      try {
        const jobMeta = await getJobResults(jobContext.jobId)
        if (jobMeta?.base_name) {
          baseName = jobMeta.base_name
          console.log(` [MAP_VIEW] Resolved baseName: "${baseName}" (context had: "${jobContext.baseName}")`)
        }
      } catch (e) {
        console.debug('[MAP_VIEW] Could not resolve baseName from /results, using context value:', baseName)
      }

      // Step 2: Load dependency mapper artifacts
      const depMapJobId = jobContext.dependencyMapperJobId || jobContext.jobs?.dependencymapper_job_id
      if (depMapJobId) {
        try {
          const depResults = await getDependencyMapperResults(depMapJobId)
          const artifacts = depResults?.json_artifacts || []

          // dependency_graph.json — class-level graph (may be 1 node for monolith)
          if (artifacts.includes('dependency_graph.json')) {
            const depGraph = await getDependencyMapperArtifact(depMapJobId, 'dependency_graph')
            if (depGraph?.nodes) setDependencyGraph(depGraph)
          } else if (depResults?.dependency_graph) {
            setDependencyGraph(depResults.dependency_graph)
          }

          // call_graph.json — method-level call hierarchy, always try to load
          if (artifacts.includes('call_graph.json')) {
            const cgData = await getDependencyMapperArtifact(depMapJobId, 'call_graph')
            if (cgData?.classes?.length) setCallGraphData(cgData)
          }
        } catch (err) {
          console.warn('[MapView] Could not load dependency mapper data:', err.message)
        }
      }

      // Step 3: Load code analysis data with authoritative baseName
      const codeAnalysisJobId = jobContext.jobId
      if (codeAnalysisJobId) {
        const [crossRef, procModel, summaryData, lineInvData] = await Promise.allSettled([
          getCrossReference(codeAnalysisJobId),
          baseName ? getProcedureModel(codeAnalysisJobId, baseName) : Promise.reject(new Error('no baseName')),
          baseName ? getSummary(codeAnalysisJobId, baseName) : Promise.reject(new Error('no baseName')),
          baseName ? getJsonArtifact(codeAnalysisJobId, `${baseName}_line_inventory.json`) : Promise.reject(new Error('no baseName')),
        ])

        if (crossRef.status === 'fulfilled') setCrossReference(crossRef.value)
        if (procModel.status === 'fulfilled') setProcedureModel(procModel.value)
        else console.debug('[MapView] procedure_model not available (optional):', procModel.reason?.message)
        if (summaryData.status === 'fulfilled') setSummary(summaryData.value)
        if (lineInvData.status === 'fulfilled') setLineInventory(lineInvData.value)
        else console.debug('[MapView] line_inventory not available (optional):', lineInvData.reason?.message)
      }
    } catch (err) {
      console.error('Failed to fetch data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [jobContext])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Computed stats from dependency graph or fallback to code analysis data
  const stats = useMemo(() => {
    if (!crossReference && !procedureModel && !callGraphData) return null

    // Resolve external calls using cross_reference.call_graph.
    // Key is "{PROGRAM}.cbl" (e.g. "CUSTUPD.cbl") — NOT the UI project/app name.
    // Try progressively: exact match, .CBL suffix, baseName.toUpperCase().cbl, first key with calls.
    const callGraphXref = crossReference?.call_graph || {}
    const mainProg = jobContext?.mainProgram || ''
    const externalCallList = (
      callGraphXref[mainProg] ||
      callGraphXref[`${mainProg}.CBL`] ||
      callGraphXref[`${mainProg}.cbl`] ||
      // Try matching by baseName from call_graph keys (e.g. "CUSTUPD.cbl")
      callGraphXref[Object.keys(callGraphXref).find(k =>
        k.toLowerCase().replace(/\.(cbl|cobol)$/, '') ===
        mainProg.toLowerCase().replace(/\.(cbl|cobol)$/, '')
      )] ||
      // Last resort: use first key that has actual calls
      callGraphXref[Object.keys(callGraphXref).find(k => (callGraphXref[k] || []).length > 0)] ||
      []
    )
    const externalCalls = Array.isArray(externalCallList) ? [...new Set(externalCallList)].length : 0
    const copybooks = Object.keys(crossReference?.copybook_usage || {}).length
    const paragraphs = procedureModel?.paragraphs || []
    const methods = callGraphData?.classes?.[0]?.methods || []

    const topParagraphs = paragraphs.length > 0
      ? paragraphs
          .map(p => ({ name: p.name, lines: (p.end_line || 0) - (p.start_line || 0) }))
          .sort((a, b) => b.lines - a.lines)
          .slice(0, 6)
      : methods.length > 0
        ? methods
            .map(m => ({ name: m.name, lines: (m.calls || []).length }))
            .sort((a, b) => b.lines - a.lines)
            .slice(0, 6)
        : []

    return {
      totalLines: lineInventory?.source_line_count || summary?.source_line_count || 0,
      paragraphs: paragraphs.length || methods.length,
      externalCalls,
      copybooks,
      topParagraphs,
    }
  }, [callGraphData, dependencyGraph, procedureModel, crossReference, summary, lineInventory, jobContext])

  const handleRunMapping = useCallback(async () => {
    try {
      await runDependencyMapping({ sourceType: 'cobol' })
    } catch (err) {
      console.error('Dependency mapping failed:', err)
    }
  }, [runDependencyMapping])

  return (
    <div className="map-view enhanced">
      {/* Header */}
      <div className="map-header">
        <div className="header-left">
          <button className="back-btn" onClick={onBack}>
            <ArrowLeft size={18} />
            <span>Back to Workflow</span>
          </button>
          <div className="view-title">
            <h2>Map: {jobContext?.mainProgram || 'Dependency Analysis'}</h2>
            <span className="view-subtitle">Interactive Call & Flow Mapping</span>
          </div>
        </div>

        <div className="header-center">
          <div className="tab-switcher">
            {Object.entries(TABS).map(([tab, config]) => {
              const Icon = config.icon
              return (
                <button
                  key={tab}
                  className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                  title={config.description}
                >
                  <Icon size={16} />
                  <span className="tab-label">{config.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        <div className="header-right"></div>
      </div>

      {/* Main content */}
      <div className="map-content">
        {!jobContext && !loading && (
          <div className="no-data-state">
            <Network size={48} />
            <h3>No Mapping Data Available</h3>
            <p>Run the Map node in your workflow first.</p>
            <button className="back-to-workflow-btn" onClick={onBack}>
              <ArrowLeft size={18} /> Back to Workflow
            </button>
          </div>
        )}

        {(jobContext || dependencyGraph) && (
          <div className="dashboard-layout">
            <div className="chart-container">
              {activeTab === 'call-graph' && (
                <ProgramStructureTab
                  callGraphData={callGraphData}
                  crossRef={crossReference}
                  mainProgram={jobContext?.mainProgram}
                  loading={loading}
                />
              )}
              {activeTab === 'cobol-java' && (
                <CobolJavaTab
                  procModel={procedureModel}
                  callGraphData={callGraphData}
                  loading={loading}
                />
              )}
              {activeTab === 'coupling' && (
                <CouplingTab
                  callGraphData={callGraphData}
                  crossRef={crossReference}
                  loading={loading}
                />
              )}
              {activeTab === 'external-calls' && (
                <ExternalCallsTab dependencyGraph={dependencyGraph} crossRef={crossReference} mainProgram={jobContext?.mainProgram} loading={loading} error={error} />
              )}
              {activeTab === 'metrics' && (
                <MetricsTab dependencyGraph={dependencyGraph} procModel={procedureModel} callGraphData={callGraphData} summary={summary} lineInventory={lineInventory} loading={loading} error={error} />
              )}
            </div>

            <div className="side-panel">
              <SummaryPanel stats={stats} loading={loading} error={error} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Wrapper with ReactFlowProvider
function MapView(props) {
  return (
    <ReactFlowProvider>
      <MapViewInner {...props} />
    </ReactFlowProvider>
  )
}

export default MapView
